/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { ICapturedTile } from '@appCanvasCommon/export/ExportCapture';
import { doesExceedCanvasLimits } from '@appCanvasCommon/export/ExportHelpers';

/**
 * Result object returned from the stitching operation.
 * Contains information about the success of the operation and the resulting canvas data.
 */
export interface IStitchResult {
	success: boolean;
	canvas?: HTMLCanvasElement;
	width?: number;
	height?: number;
	area?: number;
}

/**
 * Convenience that pairs a canvas element with its 2D rendering context.
 */
export interface ICanvasWithContext {
	canvas: HTMLCanvasElement;
	context: CanvasRenderingContext2D;
}

/**
 * Options for configuring the stitch operation
 */
export interface IStitchOptions {
	/** The margin size in pixels to add around the stitched image. Defaults to 0. */
	margin?: number;
	/**
	 * The background color for the margin area.
	 * Defaults to white (#FFFFFF) if margin is specified but backgroundColor is not.
	 */
	backgroundColor?: string;
}

/**
 * ExportStitch class handles the stitching of multiple captured tiles into a single canvas.
 * The stitching process takes tiles captured by ExportCapture and combines them into
 * a single image that represents the complete exported plan area.
 * @example
 * ```typescript
 * const stitcher = new ExportStitch();
 * // Without margin
 * const result = stitcher.stitch(capturedTiles, 4096, 3072);
 * // With 100px margin
 * const resultWithMargin = stitcher.stitch(capturedTiles, 4096, 3072, { margin: 100 });
 * if (result.success) {
 *   convertToBlobAndDownload(result.canvas);
 * } else {
 *   console.error('Canvas dimensions exceed browser limits');
 * }
 * ```
 */
export class ExportStitch {
	/**
	 * Stitch multiple captured tiles into a single canvas.
	 * This method creates a large canvas with the specified dimensions and draws each
	 * tile at its relative position to recreate the complete plan view. The tiles are
	 * positioned using their stored position data which should be relative to the
	 * first captured tile. An optional margin can be added around the entire stitched image.
	 * @param tiles - Array of captured tiles to stitch together
	 * @param width - Total width of the final stitched canvas in pixels (excluding margin)
	 * @param height - Total height of the final stitched canvas in pixels (excluding margin)
	 * @param options - Optional configuration for the stitch operation
	 * @returns IStitchResult containing success status and canvas data if successful
	 */
	public stitch(
		tiles: ICapturedTile[],
		width: number,
		height: number,
		options?: IStitchOptions
	): IStitchResult {
		const margin: number = options?.margin ?? 0;
		const backgroundColor: string = options?.backgroundColor || '#FFFFFF';

		const totalMargin: number = margin * 2;
		const canvasWidth: number = width + totalMargin;
		const canvasHeight: number = height + totalMargin;

		if (doesExceedCanvasLimits(canvasWidth, canvasHeight)) {
			return { success: false };
		}

		const stitchedCanvas: ICanvasWithContext = this.createCanvas(canvasWidth, canvasHeight);

		if (margin > 0) {
			stitchedCanvas.context.fillStyle = backgroundColor;
			stitchedCanvas.context.fillRect(0, 0, canvasWidth, canvasHeight);
		}

		tiles.forEach((tile: ICapturedTile) => {
			stitchedCanvas.context.drawImage(tile.canvas, tile.position.x + margin, tile.position.y + margin);
		});

		return {
			success: true,
			canvas: stitchedCanvas.canvas,
			width: canvasWidth,
			height: canvasHeight,
			area: canvasWidth * canvasHeight,
		};
	}

	/**
	 * Create a new canvas element with dimensions and return it with its context.
	 * This method creates an HTML canvas element, sets its dimensions, and retrieves
	 * the 2D rendering context. The canvas is configured with both pixel dimensions
	 * and CSS dimensions for proper rendering.
	 * @param width - Canvas width in pixels
	 * @param height - Canvas height in pixels
	 * @returns Object containing the canvas element and its 2D rendering context
	 * @private
	 */
	private createCanvas(width: number, height: number): ICanvasWithContext {
		const canvas: HTMLCanvasElement = window.document.createElement('canvas');
		const context: CanvasRenderingContext2D = canvas.getContext('2d') as CanvasRenderingContext2D;
		canvas.width = width;
		canvas.height = height;
		canvas.style.width = canvas.width + 'px';
		canvas.style.height = canvas.height + 'px';
		return {
			canvas,
			context,
		};
	}
}
